import type { CreateTaskBody } from 'api/models';
import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import { useCreateTaskType, useLoadTaskTypes } from 'modules/Tasks/api';
import { Controller, useFormContext } from 'react-hook-form';
import { Button, CustomSelect } from 'shared/io';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import type { RecoveryPlanStep } from 'types/recovery-plan';

import { TaskTypeForm } from '../TaskTypeForm/TaskTypeForm';

import styleIdentifiers from './TaskFormType.module.scss';

const styles = classNames.bind(styleIdentifiers);

type TaskFormTypeProps = {
  name?: 'task_type_id' | 'task_attributes.task_type_id';
  canAdd?: boolean;
  disabled?: boolean;
};

export const TaskFormType = ({
  name = 'task_type_id',
  canAdd = true,
  disabled = false,
}: TaskFormTypeProps) => {
  const { t } = useTranslation();

  const { taskTypes } = useLoadTaskTypes();

  const { setValue } = useFormContext<CreateTaskBody | RecoveryPlanStep>();

  const { createTaskType } = useCreateTaskType();

  const items = taskTypes.data.map(type => ({ id: type.id.toString(), name: type.name }));

  if (!canAdd) {
    return (
      <Controller
        rules={{ required: true }}
        name={name}
        render={() => (
          <CustomSelect
            selectClassName={styles('input')}
            keyText="name"
            keyValue="id"
            withBorder
            name={name}
            label={t(i18nKeys.TASK.ATTRIBUTES.TASK_TYPE)}
            items={items}
            notAllowed={disabled}
            filter
          />
        )}
      />
    );
  }

  const onSubmitTaskType: Parameters<typeof TaskTypeForm>[0]['onSubmit'] = data =>
    createTaskType(data, {
      onSuccess: newTaskType => {
        setValue(name, newTaskType.id.toString());
        dialogHide(DialogShowId.CUSTOM);
      },
    });

  const addTaskType = () =>
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.ADD_TASK_TYPE),
      children: (
        <TaskTypeForm
          onSubmit={onSubmitTaskType}
          onCancel={() => dialogHide(DialogShowId.CUSTOM)}
        />
      ),
    });

  return (
    <div className={styles('field-type')}>
      <Controller
        rules={{ required: true }}
        name={name}
        render={() => (
          <CustomSelect
            selectClassName={styles('input')}
            keyText="name"
            keyValue="id"
            withBorder
            name={name}
            label={t(i18nKeys.TASK.ATTRIBUTES.TASK_TYPE)}
            items={items}
            notAllowed={disabled}
            filter
          />
        )}
      />
      <Button
        label="+"
        title={t(i18nKeys.ADD_TASK_TYPE)}
        onClick={addTaskType}
        disabled={disabled}
        noMargin
      />
    </div>
  );
};
